import { matchPath } from 'react-router-dom'

import permissionService from '@app/services/permissionService'

import { PERMISSION, ROLE } from '@app/constants/permission'
import {
  CONTRACTOR_SECTION_PAGE,
  ERROR_PAGE,
  EXTERNAL_PRODUCTS_CATALOG_PAGE,
  INTERNAL_TASKS_PAGE,
  MONITORING_LAUNCH_PAGE,
  MONITORING_REGISTRY_PAGE,
  PROCESSING_RESULTS_PAGE,
  QUOTE_REPORT_PAGE,
  REGISTRY_PAGE,
  SETTINGS_PAGE,
  TRANSFER_RESULTS_PAGE
} from '@app/constants/page'

import type { Permission, Role } from '@app/types'

interface ReturnUrlAccessConfig {
  path: string
  requiredRoles?: Role[]
  requiredPermissions?: Permission[]
}

const registryRoles = [
  ROLE.priceAnalyticsSpecialist,
  ROLE.pricingSpecialist,
  ROLE.commercialServiceSpecialist,
  ROLE.promoPlanningSpecialist,
  ROLE.commercialServiceDirector
]

const processingResultsRoles = [
  ROLE.priceAnalyticsSpecialist,
  ROLE.pricingSpecialist,
  ROLE.categoryManager,
  ROLE.promoPlanningSpecialist,
  ROLE.commercialServiceDirector,
  ROLE.commercialServiceSpecialist
]

const settingsRoles = [ROLE.priceAnalyticsSpecialist]

const returnUrlAccessConfigs: ReturnUrlAccessConfig[] = [
  { path: ERROR_PAGE },
  { path: REGISTRY_PAGE.registry, requiredRoles: registryRoles },
  { path: REGISTRY_PAGE.registryId, requiredRoles: registryRoles },
  { path: REGISTRY_PAGE.registryArchivesId, requiredRoles: registryRoles },
  { path: MONITORING_LAUNCH_PAGE.monitoringLaunch, requiredRoles: [ROLE.priceAnalyticsSpecialist] },
  {
    path: MONITORING_REGISTRY_PAGE.monitoringRegistry,
    requiredRoles: [ROLE.priceAnalyticsSpecialist, ROLE.pricingSpecialist]
  },
  { path: CONTRACTOR_SECTION_PAGE.contractorSection, requiredRoles: [ROLE.contractor] },
  { path: CONTRACTOR_SECTION_PAGE.contractorSectionId, requiredRoles: [ROLE.contractor] },
  { path: PROCESSING_RESULTS_PAGE.processingResults, requiredRoles: processingResultsRoles },
  { path: PROCESSING_RESULTS_PAGE.productCard, requiredRoles: processingResultsRoles },
  {
    path: TRANSFER_RESULTS_PAGE.transferResults,
    requiredRoles: [ROLE.priceAnalyticsSpecialist, ROLE.pricingSpecialist]
  },
  { path: QUOTE_REPORT_PAGE.quoteReport, requiredRoles: registryRoles },
  {
    path: EXTERNAL_PRODUCTS_CATALOG_PAGE.externalProductsCatalog,
    requiredRoles: [
      ROLE.priceAnalyticsSpecialist,
      ROLE.pricingSpecialist,
      ROLE.categoryManager,
      ROLE.commercialServiceDirector,
      ROLE.commercialServiceSpecialist
    ]
  },
  {
    path: INTERNAL_TASKS_PAGE.internalTasks,
    requiredRoles: [
      ROLE.priceAnalyticsSpecialist,
      ROLE.pricingSpecialist,
      ROLE.commercialServiceSpecialist,
      ROLE.commercialServiceDirector,
      ROLE.promoPlanningSpecialist
    ]
  },
  { path: SETTINGS_PAGE.monitoringSchedule, requiredRoles: settingsRoles },
  { path: SETTINGS_PAGE.directoryContractors, requiredRoles: settingsRoles },
  { path: SETTINGS_PAGE.managingSubtypes, requiredRoles: settingsRoles },
  { path: SETTINGS_PAGE.managingMonitorings, requiredRoles: settingsRoles },
  { path: SETTINGS_PAGE.templatesEditor, requiredRoles: settingsRoles },
  {
    path: SETTINGS_PAGE.checkingPricesCompetitors,
    requiredRoles: [ROLE.pricingSpecialist],
    requiredPermissions: [PERMISSION.configureMonitoringParameters]
  },
  { path: SETTINGS_PAGE.classifierControls, requiredRoles: settingsRoles }
]

const getPathname = (returnUrl: string): string => {
  return returnUrl.split('?')[0].split('#')[0]
}

export const canUseReturnUrl = (returnUrl: string, userRoles: Role[] | null): boolean => {
  const pathname = getPathname(returnUrl)
  const accessConfig = returnUrlAccessConfigs.find(({ path }) => matchPath({ path, end: true }, pathname))

  if (!accessConfig) return false

  const { requiredRoles = [], requiredPermissions = [] } = accessConfig
  const isRouteAvailableForAnyUser = requiredRoles.length === 0 && requiredPermissions.length === 0

  if (isRouteAvailableForAnyUser) return true

  return permissionService.checkAccess({ userRoles, requiredRoles, requiredPermissions })
}
